import React, { useState, useEffect, useRef } from 'react';
import { BsGeoAltFill, BsExclamationCircleFill, BsCheckCircleFill } from 'react-icons/bs';
import { HiArrowPath } from 'react-icons/hi2';

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

const loadGoogleMapsScript = () => {
  return new Promise((resolve, reject) => {
    if (window.google && window.google.maps && window.google.maps.places) {
      resolve();
      return;
    }

    const existingScript = document.getElementById('google-maps-script');
    if (existingScript) {
      existingScript.addEventListener('load', resolve);
      existingScript.addEventListener('error', reject);
      return;
    }

    const script = document.createElement('script');
    script.id = 'google-maps-script';
    script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}&libraries=places`;
    script.async = true;
    script.defer = true;
    script.onload = resolve;
    script.onerror = reject;
    document.head.appendChild(script);
  });
};

const AddressInput = ({
  id,
  name,
  value,
  onChange,
  onBlur,
  label,
  error,
  touched,
  showRequired = false,
  placeholder,
  className = '',
  ...props
}) => {
  const [isGettingLocation, setIsGettingLocation] = useState(false);
  const [locationError, setLocationError] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isLoadingSuggestions, setIsLoadingSuggestions] = useState(false);
  const wrapperRef = useRef(null);
  const debounceTimer = useRef(null);
  const autocompleteService = useRef(null);
  const geocoder = useRef(null);

  const hasError = touched && error;
  const isValid = touched && !error && value;

  useEffect(() => {
    loadGoogleMapsScript()
      .then(() => {
        autocompleteService.current = new window.google.maps.places.AutocompleteService();
        geocoder.current = new window.google.maps.Geocoder();
      })
      .catch((err) => console.error('Failed to load Google Maps:', err));
  }, []);

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const getBorderColor = () => {
    if (hasError) return 'border-red-500 focus:border-red-500 focus:ring-red-500';
    if (isValid) return 'border-green-500 focus:border-green-500 focus:ring-green-500';
    return 'border-gray-300 focus:border-purple-500 focus:ring-purple-500';
  };

  const fetchSuggestions = (searchText) => {
    if (!searchText || searchText.length < 3) {
      setSuggestions([]);
      return;
    }

    if (!autocompleteService.current) return;

    setIsLoadingSuggestions(true);

    autocompleteService.current.getPlacePredictions(
      {
        input: searchText,
        componentRestrictions: { country: 'za' },
      },
      (predictions, status) => {
        setIsLoadingSuggestions(false);
        if (
          status === window.google.maps.places.PlacesServiceStatus.OK &&
          predictions
        ) {
          setSuggestions(predictions);
          setShowSuggestions(true);
        } else {
          setSuggestions([]);
        }
      }
    );
  };

  // Handle input change with debounce
  const handleInputChange = (e) => {
    const newValue = e.target.value;
    onChange(e);

    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    debounceTimer.current = setTimeout(() => {
      fetchSuggestions(newValue);
    }, 300);
  };

  // Handle suggestion selection
  const handleSuggestionClick = (suggestion) => {
    onChange({
      target: {
        id,
        name,
        value: suggestion.description,
      },
    });
    setShowSuggestions(false);
    setSuggestions([]);
  };

  const getCurrentLocation = () => {
    if (!navigator.geolocation) {
      setLocationError('Geolocation is not supported by your browser');
      return;
    }

    setIsGettingLocation(true);
    setLocationError('');

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords;

        if (!geocoder.current) {
          setLocationError('Geocoding service not ready');
          setIsGettingLocation(false);
          return;
        }

        geocoder.current.geocode(
          { location: { lat: latitude, lng: longitude } },
          (results, status) => {
            setIsGettingLocation(false);
            if (status === 'OK' && results[0]) {
              onChange({
                target: {
                  id,
                  name,
                  value: results[0].formatted_address,
                },
              });
              setShowSuggestions(false);
            } else {
              setLocationError('Could not get address from location');
            }
          }
        );
      },
      (error) => {
        setIsGettingLocation(false);
        switch (error.code) {
          case error.PERMISSION_DENIED:
            setLocationError('Location permission denied');
            break;
          case error.POSITION_UNAVAILABLE:
            setLocationError('Location information unavailable');
            break;
          case error.TIMEOUT:
            setLocationError('Location request timed out');
            break;
          default:
            setLocationError('An unknown error occurred');
        }
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0,
      }
    );
  };

  return (
    <div className="flex flex-col gap-2" ref={wrapperRef}>
      {label && (
        <label
          htmlFor={id}
          className="block text-gray-700 text-sm font-semibold"
        >
          {label} {showRequired && <span className="text-red-500">*</span>}
        </label>
      )}

      <div className="relative">
        <input
          id={id}
          name={name}
          type="text"
          value={value}
          onChange={handleInputChange}
          onBlur={onBlur}
          onFocus={() => {
            if (suggestions.length > 0) {
              setShowSuggestions(true);
            }
          }}
          placeholder={placeholder}
          autoComplete="off"
          className={`
            w-full px-4 py-3 pr-24 rounded-lg border-2
            transition-all duration-200 ease-in-out
            ${getBorderColor()}
            focus:outline-none focus:ring-2 focus:ring-opacity-50
            ${hasError ? 'bg-red-50' : isValid ? 'bg-green-50' : 'bg-white'}
            ${className}
          `}
          {...props}
        />

        {/* Use Current Location Button */}
        <button
          type="button"
          onClick={getCurrentLocation}
          disabled={isGettingLocation}
          className={`
            absolute right-2 top-1/2 transform -translate-y-1/2
            px-3 py-1.5 rounded-md text-xs font-medium
            transition-all duration-200
            ${isGettingLocation
              ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
              : 'bg-purple-600 hover:bg-purple-700 text-white'
            }
          `}
          title="Use current location"
        >
          {isGettingLocation ? (
            <span className="flex items-center gap-1">
              <HiArrowPath className="animate-spin h-3 w-3" />
            </span>
          ) : (
            <BsGeoAltFill size={14} />
          )}
        </button>

        {/* Validation icon */}
        {(hasError || isValid) && (
          <div className="absolute right-20 top-1/2 transform -translate-y-1/2">
            {hasError && (
              <BsExclamationCircleFill
                className="text-red-500 animate-pulse"
                size={20}
              />
            )}
            {isValid && (
              <BsCheckCircleFill
                className="text-green-500"
                size={20}
              />
            )}
          </div>
        )}

        {/* Autocomplete Suggestions Dropdown */}
        {showSuggestions && suggestions.length > 0 && (
          <div className="absolute z-50 w-full mt-1 bg-white border-2 border-purple-300 rounded-lg shadow-xl max-h-60 overflow-y-auto">
            {suggestions.map((suggestion) => (
              <div
                key={suggestion.place_id}
                onClick={() => handleSuggestionClick(suggestion)}
                className="px-4 py-3 hover:bg-purple-50 cursor-pointer transition-colors border-b border-gray-100 last:border-b-0"
              >
                <div className="flex items-start gap-2">
                  <BsGeoAltFill className="text-purple-600 mt-1 flex-shrink-0" size={14} />
                  <div className="flex-1">
                    <div className="text-sm font-medium text-gray-800">
                      {suggestion.structured_formatting?.main_text || suggestion.description}
                    </div>
                    {suggestion.structured_formatting?.secondary_text && (
                      <div className="text-xs text-gray-500 mt-0.5">
                        {suggestion.structured_formatting.secondary_text}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Loading indicator for suggestions */}
        {isLoadingSuggestions && (
          <div className="absolute z-50 w-full mt-1 bg-white border-2 border-gray-200 rounded-lg shadow-lg p-3">
            <div className="flex items-center gap-2 text-gray-600 text-sm">
              <HiArrowPath className="animate-spin h-4 w-4" />
              <span>Searching addresses...</span>
            </div>
          </div>
        )}
      </div>

      {/* Location error message */}
      {locationError && (
        <div className="flex items-start gap-2 text-orange-600 text-xs">
          <BsExclamationCircleFill className="mt-0.5 flex-shrink-0" size={12} />
          <span>{locationError}</span>
        </div>
      )}

      {/* Validation error message */}
      {hasError && (
        <div className="flex items-start gap-2 text-red-600 text-sm animate-slideDown">
          <BsExclamationCircleFill className="mt-0.5 flex-shrink-0" size={14} />
          <span>{error}</span>
        </div>
      )}
    </div>
  );
};

export default AddressInput;
